import { API_BASE_URL } from './Constants';

class SessionManager {
  constructor() {
    this.authStateListeners = new Set();
    this.refreshPromise = null; // To avoid multiple simultaneous refresh requests
    this.isRefreshing = false;
  }

  // Call this method to check if the user is authenticated
  isUserAuthenticated() {
    return !!localStorage.getItem("access_token");
  }

  // Call this method to validate the session with the backend
  async validateSession() {
    const token = localStorage.getItem("access_token");
    
    if (!token) {
      this.notifyAuthStateListeners(false);
      return false;
    }
    
    try {
      // Check if token refresh is needed before validating
      if (this.isTokenExpired()) {
        await this.refreshToken();
      }

      const response = await fetch(`${API_BASE_URL}/accounts/verify-token/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("user_info", JSON.stringify(data.user)); // Update user info in localStorage
        this.notifyAuthStateListeners(true);
        return true;
      } else {
        // If verification fails, try to refresh the token
        if (response.status === 401) {
          const refreshSuccess = await this.refreshToken();
          if (refreshSuccess) {
            return await this.validateSession(); // Try validation again with new token
          }
        }
        this.notifyAuthStateListeners(false);
        return false;
      }
    } catch (error) {
      console.error("Session validation error:", error);
      this.notifyAuthStateListeners(false);
      return false;
    }
  }

  // Check if access token is expired (using simple heuristic)
  // JWT tokens typically have expiry info encoded in them, but we're using a simple estimation
  isTokenExpired() {
    const token = localStorage.getItem("access_token");
    if (!token) return true;
    
    try {
      // JWT tokens are in format: header.payload.signature
      const payload = token.split('.')[1];
      if (!payload) return true;
      
      // Decode the base64 payload
      const decodedPayload = JSON.parse(atob(payload));
      
      // Check if exp (expiration time) exists and is in the past
      if (decodedPayload.exp && decodedPayload.exp * 1000 < Date.now()) {
        // console.log("Token is expired, needs refresh");
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error checking token expiration:", e);
      return true; // Assume expired if there's an error
    }
  }

  // Refresh the access token using refresh token
  async refreshToken() {
    // If already refreshing, return the existing promise to avoid multiple requests
    if (this.isRefreshing) {
      return this.refreshPromise;
    }
    
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      // console.log("No refresh token available");
      this.notifyAuthStateListeners(false);
      return false;
    }
    
    this.isRefreshing = true;
    this.refreshPromise = new Promise(async (resolve) => {
      try {
        // console.log("Attempting to refresh access token");
        const response = await fetch(`${API_BASE_URL}/accounts/token/refresh/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        
        if (response.ok) {
          const data = await response.json();
          // console.log("Token refresh successful");
          localStorage.setItem("access_token", data.access);
          
          // If a new refresh token is returned, update that too (some JWT implementations do this)
          if (data.refresh) {
            localStorage.setItem("refresh_token", data.refresh);
          }
          
          resolve(true);
          return true;
        } else {
          console.error("Failed to refresh token:", response.status);
          // If refresh fails, user needs to log in again
          this.clearSession();
          resolve(false);
          return false;
        }
      } catch (error) {
        console.error("Error refreshing token:", error);
        this.clearSession();
        resolve(false);
        return false;
      } finally {
        this.isRefreshing = false;
      }
    });
    
    return this.refreshPromise;
  }

  // Subscribe to authentication state changes
  addAuthStateListener(listener) {
    this.authStateListeners.add(listener);
  }

  // Unsubscribe from authentication state changes
  removeAuthStateListener(listener) {
    this.authStateListeners.delete(listener);
  }

  // Notify all subscribed listeners about the authentication state change
  notifyAuthStateListeners(isAuthenticated) {
    this.authStateListeners.forEach(listener => {
      try {
        listener(isAuthenticated);
      } catch (error) {
        console.error("Error in auth state listener:", error);
      }
    });
  }

  // Retrieve the current user from localStorage
  getUser() {
    const userInfo = localStorage.getItem("user_info");
    return userInfo ? JSON.parse(userInfo) : null;
  }
  
  // Clear session data on logout
  clearSession() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_info");
    this.notifyAuthStateListeners(false);
  }
}

// Create and export a singleton instance
const sessionManager = new SessionManager();
export default sessionManager;